import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';

/**
 * Grafico finanziario interattivo basato su TradingView Lightweight Charts.
 *
 * Props:
 *  - data: { ohlcv: [{time,open,high,low,close,volume}], markers: [...], equity_curve: [{time,value}], benchmark_curve: [...] }
 *  - mode: 'price' (candele + markers) | 'equity' (equity vs benchmark) | 'both' (default, split vertically)
 *  - showVolume: boolean — mostra istogramma volumi sotto le candele (default true)
 *  - height: px (default 420)
 *  - theme: 'light' | 'dark' (auto-detect via prefers-color-scheme se omesso)
 *  - liveMode: boolean — attiva bordo ring-primary per indicare live (default false)
 *  - liveSignal: object|null — il verdict response da LiveSignalBadge, per overlay trigger lines
 */
const EquityChart = ({ data, mode = 'both', showVolume = true, height = 420, theme, liveMode = false, liveSignal = null }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!containerRef.current || !data) return;

    const isDark = theme === 'dark'
      || (theme !== 'light' && typeof window !== 'undefined'
          && window.matchMedia?.('(prefers-color-scheme: dark)').matches);

    const colors = isDark
      ? { bg: 'transparent', text: '#cbd5e1', grid: '#1f2937', border: '#334155' }
      : { bg: 'transparent', text: '#475569', grid: '#e2e8f0', border: '#cbd5e1' };

    try {
      const chart = createChart(containerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: colors.bg },
          textColor: colors.text,
          fontSize: 11,
        },
        grid: {
          vertLines: { color: colors.grid },
          horzLines: { color: colors.grid },
        },
        crosshair: { mode: CrosshairMode.Normal },
        rightPriceScale: { borderColor: colors.border },
        timeScale: { borderColor: colors.border, timeVisible: false, secondsVisible: false },
        height,
        autoSize: true,
      });
      chartRef.current = chart;

      const showPricePane = mode === 'price' || mode === 'both';
      const showEquityPane = mode === 'equity' || mode === 'both';
      const renderVolume = showVolume && data.ohlcv?.length && showPricePane;

      // === PRICE PANE (candele + markers) ===
      if (showPricePane && data.ohlcv?.length) {
        const candle = chart.addCandlestickSeries({
          upColor: '#10b981',
          downColor: '#ef4444',
          borderUpColor: '#10b981',
          borderDownColor: '#ef4444',
          wickUpColor: '#10b981',
          wickDownColor: '#ef4444',
        });
        candle.setData(data.ohlcv);
        if (data.markers?.length) {
          candle.setMarkers(data.markers);
        }
        // Mantieni riferimento per le price lines live (aggiornate dall'effetto dedicato)
        candleSeriesRef.current = candle;
      }

      // === VOLUME PANE (histogram in alto, scala dedicata) ===
      // Tecnica: serie histogram con priceScaleId separato + scaleMargins che
      // confina i volumi al fondo del pane delle candele (max ~20% verticale).
      if (renderVolume) {
        const vol = chart.addHistogramSeries({
          priceFormat: { type: 'volume' },
          priceScaleId: 'volume',
          color: '#94a3b8',
        });
        vol.setData(
          data.ohlcv.map((d) => ({
            time: d.time,
            value: d.volume || 0,
            // Verde se candle bullish (close >= open), rosso altrimenti — più leggibile
            color: (d.close >= d.open) ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)',
          }))
        );
        chart.priceScale('volume').applyOptions({
          scaleMargins: { top: 0.78, bottom: 0 },
          borderColor: colors.border,
        });
      }

      // === EQUITY PANE ===
      if (showEquityPane) {
        if (data.equity_curve?.length) {
          // In modalità 'both' aggiungiamo come overlay sullo stesso pane, ma su scala separata
          const equity = chart.addLineSeries({
            color: '#3b82f6',
            lineWidth: 2,
            priceScaleId: mode === 'both' ? 'equity' : 'right',
            title: 'Strategy equity (base 100)',
          });
          equity.setData(data.equity_curve);

          if (mode === 'both') {
            chart.priceScale('equity').applyOptions({
              scaleMargins: { top: 0.05, bottom: 0.55 },
              borderColor: colors.border,
            });
            // E spingo le candele in basso (lasciando spazio per i volumi se attivi)
            chart.priceScale('right').applyOptions({
              scaleMargins: {
                top: 0.55,
                bottom: renderVolume ? 0.22 : 0.05,
              },
            });
          } else if (renderVolume) {
            // Solo equity ma volumi attivi: scala right confinata in alto
            chart.priceScale('right').applyOptions({
              scaleMargins: { top: 0.05, bottom: 0.22 },
            });
          }
        }
        if (data.benchmark_curve?.length) {
          const bench = chart.addLineSeries({
            color: '#94a3b8',
            lineWidth: 1,
            lineStyle: 2, // dashed
            priceScaleId: mode === 'both' ? 'equity' : 'right',
            title: 'Benchmark (base 100)',
          });
          bench.setData(data.benchmark_curve);
        }
      } else if (renderVolume) {
        // Solo price: confino le candele lasciando spazio sotto per i volumi
        chart.priceScale('right').applyOptions({
          scaleMargins: { top: 0.05, bottom: 0.22 },
        });
      }

      chart.timeScale().fitContent();

      const handleResize = () => {
        if (containerRef.current) {
          chart.applyOptions({ width: containerRef.current.clientWidth });
        }
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        candleSeriesRef.current = null;
        chart.remove();
        chartRef.current = null;
      };
    } catch (e) {
      setError(e.message || 'Errore rendering chart');
    }
  }, [data, mode, showVolume, height, theme]);

  // Effetto separato per le trigger lines live — si aggiorna senza ricostruire il chart
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;

    const lines = [];

    if (liveMode && liveSignal) {
      if (liveSignal.trigger_long != null) {
        const longLine = series.createPriceLine({
          price: liveSignal.trigger_long,
          color: '#10b981',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: `LONG @ ${Number(liveSignal.trigger_long).toFixed(2)}`,
        });
        lines.push(longLine);
      }
      if (liveSignal.trigger_exit != null) {
        const exitLine = series.createPriceLine({
          price: liveSignal.trigger_exit,
          color: '#ef4444',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: `EXIT @ ${Number(liveSignal.trigger_exit).toFixed(2)}`,
        });
        lines.push(exitLine);
      }
    }

    // Cleanup: rimuovi le linee quando liveMode va false o signal cambia
    return () => {
      if (series && lines.length) {
        lines.forEach((line) => {
          try { series.removePriceLine(line); } catch (_) { /* ignorato se chart già smontato */ }
        });
      }
    };
  }, [liveMode, liveSignal]);

  if (error) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-red-500 border border-dashed rounded-lg">
        Errore chart: {error}
      </div>
    );
  }

  if (!data || (!data.ohlcv?.length && !data.equity_curve?.length)) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-muted-foreground border border-dashed rounded-lg">
        Nessun dato disponibile. Esegui <code className="mx-1 font-mono text-xs">python scripts/export_series.py --all</code> nel repo Quant Lab.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: `${height}px` }}
      className={liveMode ? 'ring-2 ring-primary rounded-sm transition-all' : undefined}
    />
  );
};

export default EquityChart;
